import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Save, ListChecks } from "lucide-react";
import axios from "axios";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const toLines = (html) => (String(html || "").match(/<li>([\s\S]*?)<\/li>/g) || []).map((x) => x.replace(/<\/?li>/g, "").replace(/<[^>]+>/g, "").trim()).join("\n");
const toHtml = (txt) => "<ol>" + txt.split("\n").map((l) => l.replace(/^[\s\d.\-•*]+/, "").trim()).filter(Boolean).map((l) => `<li>${l.replace(/[<>]/g, "")}</li>`).join("") + "</ol>";

const JobRow = ({ it, onSaved }) => {
  const [txt, setTxt] = useState(toLines(it.html));
  const [busy, setBusy] = useState("");
  const dirty = txt !== toLines(it.html);
  const gen = async () => {
    setBusy("gen");
    try { const r = await axios.post(`${API}/job-duties/generate`, { job: it.job, html: txt ? "regen" : "" }); setTxt(toLines(r.data.html)); }
    catch { toast.error("تعذّر التوليد"); } finally { setBusy(""); }
  };
  const save = async () => {
    setBusy("save");
    try { const r = await axios.put(`${API}/job-duties`, { job: it.job, html: toHtml(txt) }); toast.success(`حُفظت مهام «${it.job}»${r.data.letters_updated ? ` وحُدّث ${r.data.letters_updated} خطاب` : ""}`); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(""); }
  };
  return (
    <div className="rounded-xl border border-slate-200 p-3" data-testid={`duties-row-${it.job}`}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="font-heading text-sm font-bold">{it.job} <span className="text-xs font-normal text-slate-400">· {it.employees} موظف</span>
          {it.edited && <span className="mr-2 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">معدّلة</span>}</p>
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={gen} disabled={!!busy} data-testid={`duties-gen-${it.job}`}>{busy === "gen" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {txt ? "إعادة صياغة" : "صياغة بالذكاء الاصطناعي"}</Button>
          <Button size="sm" className="h-7 gap-1 text-xs" onClick={save} disabled={!!busy || !txt.trim() || (!dirty && it.edited)} data-testid={`duties-save-${it.job}`}>{busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} حفظ</Button>
        </div>
      </div>
      <Textarea rows={Math.min(10, Math.max(3, txt.split("\n").length))} value={txt} onChange={(e) => setTxt(e.target.value)} placeholder="كل مهمة في سطر…" className="text-sm leading-7" data-testid={`duties-text-${it.job}`} />
    </div>
  );
};

export const JobDutiesDialog = ({ open, onOpenChange }) => {
  const [items, setItems] = useState(null);
  const load = () => axios.get(`${API}/job-duties`).then((r) => setItems(r.data.items)).catch(() => toast.error("تعذّر التحميل"));
  useEffect(() => { if (open) load(); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="job-duties-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ListChecks className="h-5 w-5 text-emerald-600" /> مهام الوظائف</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">راجع مهام كل وظيفة مرة واحدة — تُستخدم في خطابات المهام لكل الموظفين بنفس الوظيفة، والحفظ يحدّث خطاباتهم السابقة غير الموقّعة.</p>
        {!items ? <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /> : <div className="space-y-3">{items.map((it) => <JobRow key={it.job} it={it} onSaved={load} />)}</div>}
      </DialogContent>
    </Dialog>
  );
};
